import { z } from 'zod';

import { emailSchema } from 'features/onboarding/schemas/primitives';
import {
  BUSINESS_CATEGORIES,
  DELIVERY_COMPANIES,
  KOSHER_CERTIFICATIONS,
  SENSITIVITIES,
} from 'features/onboarding/types/onboarding.types';

/**
 * Second wizard step: everything about the business itself — category and
 * store type, kosher / competitor questions, and the remaining contact
 * channels. Spans the `businessInfo` and `businessQuestions` sections.
 * Working days and opening hours are collected on the review step.
 *
 * Kosher certifications and delivery platforms are optional selections: an
 * empty list is a valid answer meaning "none", and `isKosher` /
 * `worksWithOtherDeliveryCompanies` are derived from them by the step.
 *
 * `location` is chosen here via the city select. `address` is kept from the
 * map pin on the contact step and is optional on this step (the pin may
 * resolve without a street line).
 */
export const stepBusinessSchema = z
  .object({
    businessCategory: z.enum(BUSINESS_CATEGORIES, {
      errorMap: () => ({ message: 'required' }),
    }),
    storeType: z.string().min(1, 'required'),
    storeTypeOther: z.string().optional().or(z.literal('')),
    location: z.string().min(1, 'required'),
    address: z.string().optional().or(z.literal('')),
    kosherCertifications: z.array(z.enum(KOSHER_CERTIFICATIONS)).default([]),
    sensitivities: z.array(z.enum(SENSITIVITIES)).default([]),
    deliveryCompanies: z.array(z.enum(DELIVERY_COMPANIES)).default([]),
    otherDeliveryCompanies: z.string().optional().or(z.literal('')),
    businessPhoneNumber: z.string().optional().or(z.literal('')),
    email: emailSchema,
  })
  .refine(
    (d) =>
      !d.deliveryCompanies.includes('other') ||
      (d.otherDeliveryCompanies?.trim().length ?? 0) > 0,
    { path: ['otherDeliveryCompanies'], message: 'required' },
  )
  .refine(
    (d) => {
      const isOther =
        d.storeType.trim().toLowerCase() === 'other' ||
        d.storeType.trim() === 'אחר';
      return !isOther || (d.storeTypeOther?.trim().length ?? 0) > 0;
    },
    { path: ['storeTypeOther'], message: 'required' },
  );

export type StepBusinessInput = z.infer<typeof stepBusinessSchema>;
